import React from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import BedroomParentOutlinedIcon from "@mui/icons-material/BedroomParentOutlined";
import ViewCompactAltOutlinedIcon from "@mui/icons-material/ViewCompactAltOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StarsOutlinedIcon from "@mui/icons-material/StarsOutlined";
import RatingSummary from "./RatingSummaryCard";
import CommentCard from "./CommentCard";
import RoomList, { RoomOption } from "./RoomList";
import AmenityList from "./AmenityList";
import HotelOverviewCard from "./HotelOverviewCard";
import DoneAllIcon from "@mui/icons-material/DoneAll";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface HotelDetailTabCardProps {
  hotel: any;
  roomDetails?: any;
  onBookRoom?: (room: RoomOption) => void;
}

const ratingBreakdown = [
  { label: "Excellent", percentage: 69 },
  { label: "Very Good", percentage: 18 },
  { label: "Average", percentage: 7 },
  { label: "Poor", percentage: 3 },
  { label: "Bad", percentage: 3 },
];

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const HotelDetailTabCard: React.FC<HotelDetailTabCardProps> = ({
  hotel,
  roomDetails,
  onBookRoom,
}) => {
  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const roomData = roomDetails?.AvailableRoom || hotel?.AvailableRoom || [];
  const image = roomDetails?.ImageThumbUrl || hotel?.ImageThumbUrl;
  const EMTCommonId = roomDetails?.EMTCommonId || hotel?.EMTCommonId;
  const latitude = hotel?.latitude || hotel?.Latitude;
  const longitude = hotel?.longtitude || hotel?.Longitude;

  return (
    <div className="card my-3 hotel-detail-tabs ancillary-services-card">
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="hotel tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              icon={<BedroomParentOutlinedIcon />}
              label="Rooms"
              {...a11yProps(0)}
              iconPosition="start"
            />
            <Tab
              icon={<ViewCompactAltOutlinedIcon />}
              label="Overview"
              {...a11yProps(1)}
              iconPosition="start"
            />
            <Tab
              icon={<HotelOutlinedIcon />}
              label="Hotel Photos"
              {...a11yProps(2)}
              iconPosition="start"
            />
            <Tab
              icon={<LocationOnOutlinedIcon />}
              label="Location"
              {...a11yProps(3)}
              iconPosition="start"
            />
            <Tab
              icon={<StarsOutlinedIcon />}
              label="Amenities"
              {...a11yProps(4)}
              iconPosition="start"
            />
            <Tab
              icon={<StarsOutlinedIcon />}
              label="Guest Rating"
              {...a11yProps(5)}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <Box
          sx={{
            height: "auto",
            overflowY: "auto",
            overflowX: "hidden",
            p: 0,
            maxHeight: "600px",
          }}
        >
          <CustomTabPanel value={value} index={0}>
            <RoomList
              rooms={hotel.AvailableRoom}
              hotelImageUrl={hotel.ImageThumbUrl}
              onBookRoom={onBookRoom} // Pass both
            />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={1}>
            <HotelOverviewCard hotel={hotel} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={2}>
            <h1 className="tab-title">Hotel Photos</h1>
            <AmenityList EMTCommonID={"EMTHOTEL-4521578"} />
          </CustomTabPanel>

          <CustomTabPanel value={value} index={3}>
            <h2 className="tab-title">Location</h2>
            {latitude && longitude ? (
              <div
                className="relative w-full overflow-hidden rounded-lg border"
                style={{ height: "420px" }}
              >
                <iframe
                  title="Google Map"
                  src={`https://www.google.com/maps?q=${parseFloat(
                    latitude
                  )},${parseFloat(longitude)}&z=14&output=embed`}
                  width="100%"
                  height="100%"
                  loading="lazy"
                  style={{ border: 0 }}
                  allowFullScreen
                ></iframe>
              </div>
            ) : (
              <p className="text-gray-600 italic">Location not available</p>
            )}
          </CustomTabPanel>

          <CustomTabPanel value={value} index={4}>
            <div className="mt-2">
              <h3 className="h5 fw-semibold mb-3">Room Amenities</h3>
              <div className="row row-cols-2 row-cols-md-4 g-1">
                <div className="col ">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Bath className="amenity-icon" /> */}
                    <span className="text-secondary">
                      <DoneAllIcon /> Free toiletries
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Iron className="amenity-icon" /> */}
                    <span className="text-secondary">
                      <DoneAllIcon /> Ironing facilities
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Car className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> Parking facility
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Briefcase className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> Luggage Storage
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Clock className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> 24/7 Front Desk
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Coffee className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> Restaurant
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Wifi className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> Free Wi-Fi
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Tv className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> Flat-screen TV
                    </span>
                  </div>
                </div>
                <div className="col">
                  <div className="d-flex align-items-center gap-2">
                    {/* <Phone className="amenity-icon" /> */}
                    <span className="text-secondary">
                      {" "}
                      <DoneAllIcon /> 24/7 Support
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </CustomTabPanel>

          <CustomTabPanel value={value} index={5}>
            <div className="row">
              <h2 className="tab-title">Rating Category</h2>
              <div className="col-md-4">
                <RatingSummary
                  averageRating={parseFloat(hotel?.rating || "4.5")}
                  totalRatings={410}
                  totalReviews={hotel?.tripAdvisorReviewCount || 0}
                  breakdown={ratingBreakdown}
                />
              </div>
              <div className="col-md-8">
                <CommentCard
                  avatarSrc="/images/drink.png"
                  username="Good stay and good room"
                  timeAgo="4h"
                  comment="Room was neat and clean. Staff was courteous and responsive."
                />
                <CommentCard
                  avatarSrc="/images/drink.png"
                  username="Good Hospitality"
                  timeAgo="2d"
                  comment="The location was central, and amenities were up to the mark."
                />
                <CommentCard
                  avatarSrc="/images/drink.png"
                  username="Best for business trips"
                  timeAgo="1w"
                  comment="Facilities were apt for meetings and professional stays."
                />
              </div>
            </div>
          </CustomTabPanel>
        </Box>
      </Box>
    </div>
  );
};

export default HotelDetailTabCard;
